import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  BarChart3, TrendingUp, Eye, MousePointer, Film, Trophy,
  Flame, Target, Loader2, RefreshCw, Star, Layers,
  ArrowUpRight, Users, Zap, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AppShell from '../components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, sub, color = 'text-zinc-100' }) => (
  <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="text-xs text-zinc-500 font-medium">{label}</span>
    </div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    {sub && <div className="text-xs text-zinc-600 mt-0.5">{sub}</div>}
  </div>
);

const BarInline = ({ value, max, color }) => (
  <div className="flex items-center gap-2 flex-1">
    <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, background: color }} />
    </div>
    <span className="text-xs text-zinc-400 w-8 text-right font-mono">{value}</span>
  </div>
);

const IndicatorBadge = ({ type }) => {
  const cfg = {
    score: { label: 'Top Score', bg: 'bg-amber-500/15 text-amber-400', icon: Star },
    views: { label: 'Most Viewed', bg: 'bg-orange-500/15 text-orange-400', icon: Flame },
    clicks: { label: 'Most Clicked', bg: 'bg-emerald-500/15 text-emerald-400', icon: Target },
  };
  const c = cfg[type];
  if (!c) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${c.bg}`}>
      <c.icon className="w-2.5 h-2.5" />{c.label}
    </span>
  );
};

export default function CampaignAnalyticsPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/campaign-insights`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(resp.data);
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 401) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load analytics');
      }
    }
    setLoading(false);
  };

  useEffect(() => { fetchInsights(); }, []);

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </AppShell>
    );
  }

  if (!data) {
    return (
      <AppShell>
        <div className="max-w-5xl mx-auto px-4 py-12 text-center text-zinc-500">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Could not load campaign analytics. Admin access required.</p>
        </div>
      </AppShell>
    );
  }

  const { pattern_insights: pi, engagement: eng, framework_performance: fwPerf, platform_performance: platPerf, campaign_summaries: camps, creative_leaderboard: board } = data;
  const maxFwScore = Math.max(...(fwPerf || []).map(f => f.avg_score), 1);
  const maxFwViews = Math.max(...(fwPerf || []).map(f => f.total_views), 1);
  const maxFwClicks = Math.max(...(fwPerf || []).map(f => f.total_clicks), 1);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-zinc-100" data-testid="analytics-title">Campaign Analytics</h1>
            <p className="text-sm text-zinc-500 mt-1">Performance insights across all campaigns</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchInsights} data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>

        {/* Top-level Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="top-stats">
          <StatCard icon={Layers} label="Campaigns" value={pi.total_campaigns} color="text-zinc-100" />
          <StatCard icon={Zap} label="Creatives" value={pi.total_creatives} color="text-blue-400" />
          <StatCard icon={Eye} label="Views" value={eng.total_views} color="text-orange-400" />
          <StatCard icon={MousePointer} label="Clicks" value={eng.total_clicks} sub={`${eng.click_rate}% CTR`} color="text-emerald-400" />
          <StatCard icon={Film} label="Rendered" value={pi.total_rendered} color="text-purple-400" />
          <StatCard icon={Star} label="Avg Score" value={pi.avg_creative_score} color="text-amber-400" />
        </div>

        {/* Pattern Insights Banner */}
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 mb-6" data-testid="pattern-insights">
          <h2 className="text-base font-semibold text-zinc-100 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-orange-500" /> Creative Pattern Insights
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-0.5">Top Framework</p>
              <p className="text-sm font-semibold text-zinc-200">{pi.top_framework}</p>
            </div>
            <div>
              <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-0.5">Most Clicked Framework</p>
              <p className="text-sm font-semibold text-zinc-200">{pi.most_clicked_framework}</p>
            </div>
            <div>
              <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-0.5">Most Viewed Platform</p>
              <p className="text-sm font-semibold text-zinc-200 capitalize">{pi.most_viewed_platform}</p>
            </div>
            <div>
              <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-0.5">Score Distribution</p>
              <div className="flex gap-1 items-end h-6">
                {Object.entries(pi.score_distribution).map(([bucket, count]) => (
                  <div key={bucket} className="flex flex-col items-center gap-0.5" title={`${bucket}: ${count}`}>
                    <div className="w-4 bg-orange-500/60 rounded-sm" style={{ height: `${Math.max(2, (count / Math.max(...Object.values(pi.score_distribution), 1)) * 24)}px` }} />
                    <span className="text-[8px] text-zinc-600">{bucket.replace('below_', '<')}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {[
            { key: 'overview', label: 'Campaign Performance', icon: BarChart3 },
            { key: 'leaderboard', label: 'Creative Leaderboard', icon: Trophy },
            { key: 'frameworks', label: 'Framework Performance', icon: TrendingUp },
          ].map(t => (
            <Button key={t.key} variant={tab === t.key ? 'default' : 'outline'} size="sm" onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5 mr-1" />{t.label}
            </Button>
          ))}
        </div>

        {/* CAMPAIGN PERFORMANCE TABLE */}
        {tab === 'overview' && (
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl overflow-hidden" data-testid="campaign-performance-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-xs text-zinc-500 font-medium px-4 py-3">Campaign</th>
                  <th className="text-center text-xs text-zinc-500 font-medium px-3 py-3">Views</th>
                  <th className="text-center text-xs text-zinc-500 font-medium px-3 py-3">Clicks</th>
                  <th className="text-center text-xs text-zinc-500 font-medium px-3 py-3">Rendered</th>
                  <th className="text-left text-xs text-zinc-500 font-medium px-3 py-3">Winner</th>
                  <th className="text-left text-xs text-zinc-500 font-medium px-3 py-3">Framework</th>
                </tr>
              </thead>
              <tbody>
                {camps.map((c, i) => (
                  <tr key={c.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-zinc-200 font-medium truncate max-w-[200px]">{c.product}</p>
                      <p className="text-[11px] text-zinc-600 capitalize">{c.platform} · {c.total_creatives} creatives</p>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className="text-zinc-300 font-mono">{c.total_views}</span>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className="text-zinc-300 font-mono">{c.total_clicks}</span>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className={`font-mono ${c.rendered_videos > 0 ? 'text-green-400' : 'text-zinc-600'}`}>{c.rendered_videos}/{c.total_creatives}</span>
                    </td>
                    <td className="px-3 py-3">
                      {c.top_creative_letter && (
                        <span className="text-amber-400 font-medium">Creative {c.top_creative_letter}
                          <span className="text-zinc-500 text-xs ml-1">({c.top_creative_score})</span>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded">{c.winning_framework || 'N/A'}</span>
                    </td>
                  </tr>
                ))}
                {camps.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-zinc-600">No completed campaigns yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* CREATIVE LEADERBOARD */}
        {tab === 'leaderboard' && (
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl overflow-hidden" data-testid="creative-leaderboard">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-center text-xs text-zinc-500 font-medium px-3 py-3 w-10">#</th>
                  <th className="text-left text-xs text-zinc-500 font-medium px-3 py-3">Creative</th>
                  <th className="text-center text-xs text-zinc-500 font-medium px-3 py-3">Score</th>
                  <th className="text-center text-xs text-zinc-500 font-medium px-3 py-3">Views</th>
                  <th className="text-center text-xs text-zinc-500 font-medium px-3 py-3">Clicks</th>
                  <th className="text-center text-xs text-zinc-500 font-medium px-3 py-3">Status</th>
                  <th className="text-left text-xs text-zinc-500 font-medium px-3 py-3">Framework</th>
                  <th className="text-left text-xs text-zinc-500 font-medium px-3 py-3">Badges</th>
                </tr>
              </thead>
              <tbody>
                {board.map((c, i) => (
                  <tr key={c.id} className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors ${i === 0 ? 'bg-amber-500/5' : ''}`}>
                    <td className="text-center px-3 py-3">
                      <span className={`font-mono text-xs ${i === 0 ? 'text-amber-400 font-bold' : 'text-zinc-600'}`}>{i + 1}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-zinc-200 font-medium">Creative {c.letter}</span>
                      <span className="text-zinc-600 text-[10px] ml-1 capitalize">{c.platform}</span>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className={`font-bold font-mono ${c.score >= 85 ? 'text-green-400' : c.score >= 75 ? 'text-amber-400' : 'text-zinc-400'}`}>{c.score}</span>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className="text-zinc-300 font-mono">{c.views}</span>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className="text-zinc-300 font-mono">{c.clicks}</span>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        c.video_status === 'ready' ? 'bg-green-500/10 text-green-400' :
                        c.video_status === 'rendering' ? 'bg-blue-500/10 text-blue-400' :
                        'bg-zinc-800 text-zinc-600'
                      }`}>
                        {c.video_status === 'ready' ? 'Rendered' : c.video_status === 'rendering' ? 'Rendering' : 'No Video'}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-zinc-400">{c.framework}</span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {c.is_top_score && <IndicatorBadge type="score" />}
                        {c.is_most_viewed && <IndicatorBadge type="views" />}
                        {c.is_most_clicked && <IndicatorBadge type="clicks" />}
                      </div>
                    </td>
                  </tr>
                ))}
                {board.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-8 text-zinc-600">No scored creatives yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* FRAMEWORK PERFORMANCE */}
        {tab === 'frameworks' && (
          <div className="space-y-4" data-testid="framework-performance">
            {/* Framework Table */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left text-xs text-zinc-500 font-medium px-4 py-3">Framework</th>
                    <th className="text-left text-xs text-zinc-500 font-medium px-3 py-3">Avg Score</th>
                    <th className="text-left text-xs text-zinc-500 font-medium px-3 py-3">Total Views</th>
                    <th className="text-left text-xs text-zinc-500 font-medium px-3 py-3">Total Clicks</th>
                    <th className="text-center text-xs text-zinc-500 font-medium px-3 py-3">Creatives</th>
                  </tr>
                </thead>
                <tbody>
                  {fwPerf.map((fw, i) => (
                    <tr key={fw.framework} className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors ${i === 0 ? 'bg-amber-500/5' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {i === 0 && <Trophy className="w-3.5 h-3.5 text-amber-400" />}
                          <span className="text-zinc-200 font-medium">{fw.framework}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <BarInline value={fw.avg_score} max={maxFwScore} color="#f59e0b" />
                      </td>
                      <td className="px-3 py-3">
                        <BarInline value={fw.total_views} max={maxFwViews} color="#f97316" />
                      </td>
                      <td className="px-3 py-3">
                        <BarInline value={fw.total_clicks} max={maxFwClicks} color="#22c55e" />
                      </td>
                      <td className="text-center px-3 py-3">
                        <span className="text-zinc-400 font-mono text-xs">{fw.creative_count}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Platform Performance */}
            {platPerf.length > 0 && (
              <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" /> Platform Performance
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {platPerf.map(p => (
                    <div key={p.platform} className="bg-zinc-800/40 rounded-xl p-3 border border-zinc-800">
                      <p className="text-sm font-semibold text-zinc-200 capitalize mb-2">{p.platform}</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-zinc-500">Campaigns</span>
                          <p className="text-zinc-300 font-mono">{p.campaigns}</p>
                        </div>
                        <div>
                          <span className="text-zinc-500">Avg Score</span>
                          <p className="text-amber-400 font-mono">{p.avg_score}</p>
                        </div>
                        <div>
                          <span className="text-zinc-500">Views</span>
                          <p className="text-orange-400 font-mono">{p.total_views}</p>
                        </div>
                        <div>
                          <span className="text-zinc-500">Clicks</span>
                          <p className="text-emerald-400 font-mono">{p.total_clicks}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
